import { assocPath, path as findValueByPath } from "ramda";
import React from "react";
import { MessageDescriptor } from "react-intl";

import { Presence, Value } from "lib/build/schema";

import { useFormatMessageMap } from "src/common/hooks/useFormatMessageMap";
import { FormContext } from "src/features/forms/context/FormContext";
import { LogContext } from "src/features/forms/context/LogContext";
import { NodeContext } from "src/features/forms/context/NodeContext";
import { LogProps } from "src/features/forms/types";

const emptyLogProps: LogProps = {};

export function useRequired(): boolean {
	const messageProps = useMessageProps();
	const presence = useLogProp<Presence>(messageProps, "presence", "required");
	return presence === "required";
}

export function useMinItems(): number {
	const messageProps = useMessageProps();
	return useLogProp<number>(messageProps, "minItems", 0);
}

export function useMaxItems(): number {
	const messageProps = useMessageProps();
	return useLogProp<number>(messageProps, "maxItems", Infinity);
}

export function useValue<T>(defaultValue: T): readonly [T, (value: T) => void] {
	const { value: root, setValue: setRoot } = React.useContext(FormContext);
	const { path } = React.useContext(NodeContext);

	const defaultValueRef = React.useRef(defaultValue);
	const value = React.useMemo(() => {
		const value = findValueByPath(path, root);
		return value === undefined ? defaultValueRef.current : (value as T);
	}, [root, path]);

	const onChangeValue = React.useCallback(
		(value: T) => {
			setRoot((root: Value) => assocPath(path, value, root));
		},
		[setRoot, path]
	);

	return [value, onChangeValue];
}

export function useError(messages: {
	[key: string]: MessageDescriptor;
}): string | undefined {
	const formatMessage = useFormatMessageMap();
	const errorProps = useErrorProps();
	return React.useMemo(() => {
		const keys = Object.keys(errorProps);
		if (keys.length === 0) {
			return;
		}
		const [key] = keys;
		if (!(key in messages)) {
			return;
		}
		return formatMessage(messages, key, { [key]: errorProps[key] as any });
	}, [formatMessage, messages, errorProps]);
}

export function useMessageProps(): LogProps {
	const { messages } = React.useContext(LogContext);
	const { path } = React.useContext(NodeContext);
	return React.useMemo(
		() => messages[path.join("/")] || emptyLogProps,
		[messages, path]
	);
}

export function useErrorProps(): LogProps {
	const { errors } = React.useContext(LogContext);
	const { path } = React.useContext(NodeContext);
	return React.useMemo(() => {
		const pathStr = path.join("/");
		for (const key of Object.keys(errors)) {
			if (key.startsWith(pathStr)) {
				return errors[key];
			}
		}
		return emptyLogProps;
	}, [errors, path]);
}

export function useLogProp<T>(
	props: LogProps,
	key: string,
	defaultValue: T
): T {
	return React.useMemo(
		() => (key in props ? (props[key] as T) : defaultValue),
		[props, key, defaultValue]
	);
}
